import {
  api,
  API_PREFIX,
  buildPath,
  type BodyOf,
  type EndpointName,
  type QueryOf,
  type ResponseOf,
} from './api.js';
import type { ApiError } from './schemas.js';

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly error: ApiError,
  ) {
    super(error.message);
    this.name = 'ApiRequestError';
  }
}

export interface ClientOptions {
  baseUrl?: string;
  getToken?: () => string | null;
  /** Chamado em 401: tenta renovar o token e devolve true para repetir. */
  onUnauthorized?: () => Promise<boolean>;
  fetch?: typeof fetch;
}

export type CallArgs<K extends EndpointName> = {
  params?: Record<string, string | number>;
} & (BodyOf<K> extends undefined ? { body?: undefined } : { body: BodyOf<K> }) &
  (QueryOf<K> extends undefined ? { query?: undefined } : { query?: QueryOf<K> });

/** Cliente tipado gerado a partir do contrato. */
export function createApiClient(options: ClientOptions = {}) {
  const doFetch = options.fetch ?? fetch;
  async function call<K extends EndpointName>(name: K, args?: CallArgs<K>): Promise<ResponseOf<K>> {
    const endpoint = api[name];
    let url = `${options.baseUrl ?? ''}${API_PREFIX}${buildPath(endpoint.path, args?.params)}`;
    const query = (args as { query?: Record<string, unknown> } | undefined)?.query;
    if (query) {
      const qs = new URLSearchParams();
      for (const [k, v] of Object.entries(query))
        if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
      const s = qs.toString();
      if (s) url += `?${s}`;
    }
    const run = async () => {
      const headers: Record<string, string> = {};
      const token = options.getToken?.();
      if (token) headers.Authorization = `Bearer ${token}`;
      const body = (args as { body?: unknown } | undefined)?.body;
      if (body !== undefined) headers['Content-Type'] = 'application/json';
      return doFetch(url, {
        method: endpoint.method,
        headers,
        credentials: 'include',
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      });
    };
    let res = await run();
    if (res.status === 401 && endpoint.auth && options.onUnauthorized && (await options.onUnauthorized()))
      res = await run();
    const text = await res.text();
    const data: unknown = text ? JSON.parse(text) : undefined;
    if (!res.ok) {
      const err = (data ?? { code: 'HTTP_ERROR', message: res.statusText }) as ApiError;
      throw new ApiRequestError(res.status, err);
    }
    return data as ResponseOf<K>;
  }
  return { call };
}

export type ApiClient = ReturnType<typeof createApiClient>;
